import {
  RouterProvider,
  createBrowserRouter,
  redirect,
} from 'react-router-dom';

import Loader from 'components/Layout/loader';
import ErrorComponent from 'components/errorComponent';
import { CustomerContext, useCustomerManager } from 'hooks/useCustomer';
import { NavigationRoutes } from 'utils/const';
import React from 'react';
import { PermissionEnum } from 'utils/enums';
import PermissionRoute from './permissionRoute';

const WalkupLayout = React.lazy(() => import('components/Layout/walkupLayout'));
const PersistentLayout = React.lazy(() => import('components/Layout'));
const OrderLookUp = React.lazy(() => import('pages/Others/dashboard'));
const Dashboard = React.lazy(() => import('pages/Others/dashboard'));
const StaffPointsPage = React.lazy(() => import('pages/user/staffPointsPage'));
const EditStaffPage = React.lazy(() => import('pages/admin/editStaffPage'));
const EditCustomerPage = React.lazy(
  () => import('pages/crm/customer/editCustomerPage'),
);
const CustomerMasterList = React.lazy(
  () => import('pages/crm/customer/customerMasterList'),
);
const ProductionStationListPage = React.lazy(
  () => import('pages/admin/productionStationListPage'),
);
const ModifyPartAssembly = React.lazy(
  () => import('pages/managment/assembly/modifyPartAssembly'),
);
const PartsListPage = React.lazy(
  () => import('pages/managment/parts/partsListPage'),
);
const ProductionProcess = React.lazy(
  () => import('pages/admin/productionProcess'),
);
const PlyList = React.lazy(() => import('pages/admin/plyList'));
const MaterialList = React.lazy(() => import('pages/admin/materialList'));

const Colors = React.lazy(() => import('pages/admin/colors'));
const CarrierList = React.lazy(() => import('pages/admin/carrierList'));
const PartAssemblyList = React.lazy(
  () => import('pages/managment/assembly/partAssemblyList'),
);
const OrderItem = React.lazy(() => import('pages/Others/order/orderItem'));
const OrderListPage = React.lazy(
  () => import('pages/Others/order/orderListPage'),
);
const ContactListPage = React.lazy(
  () => import('pages/crm/customer/tabs/contactListPage'),
);
const PackagingItem = React.lazy(
  () => import('pages/Others/packaging/packagingItem'),
);
const OrderRegions = React.lazy(() => import('pages/maps/orderRegions'));
const CustomerRegions = React.lazy(() => import('pages/maps/customerRegions'));

// Lazy load all page components
const PackagingScreen = React.lazy(
  () => import('pages/Others/packaging/packagingScreen'),
);
const ShipmentItem = React.lazy(
  () => import('pages/Others/shipping/shipmentItem'),
);
const Login = React.lazy(() => import('pages/user/login'));
const WalkupLogin = React.lazy(() => import('pages/user/walkupLogin'));
const ShippingScreen = React.lazy(
  () => import('pages/Others/shipping/shipmentScreen'),
);

const RoleListPage = React.lazy(() => import('pages/admin/role/roleListPage'));
const EditRolePermission = React.lazy(
  () => import('pages/admin/role/editRolePermission'),
);
const ProductionStatus = React.lazy(
  () => import('pages/Others/productionScreen/productionScreen'),
);
const CarouselImages = React.lazy(
  () => import('pages/webDetails/carouselImages'),
);
const ChangePassword = React.lazy(() => import('pages/user/changePassword'));
const ProductionSchedule = React.lazy(
  () => import('pages/managment/productionSchedule'),
);
const AttachmentListPage = React.lazy(
  () => import('pages/admin/attachmentListPage'),
);
const CommodityDensityMappingList = React.lazy(
  () => import('pages/admin/commodityDensityMappingList'),
);
const Leads = React.lazy(() => import('pages/crm/leads'));
const Sources = React.lazy(() => import('pages/crm/sources'));
const Opportunity = React.lazy(() => import('pages/crm/opportunity'));
const Holiday = React.lazy(() => import('pages/admin/holiday'));
const ConvertToOpportunity = React.lazy(
  () => import('pages/crm/convertToOpportunity'),
);
const WebOrders = React.lazy(() => import('pages/crm/webOrders'));
const CostMethod = React.lazy(() => import('pages/crm/costMethod'));
const ComponetMaster = React.lazy(() => import('pages/crm/componetMaster'));
const PartComponent = React.lazy(() => import('pages/crm/partComponent'));
const ColumnSelection = React.lazy(() => import('pages/admin/columnSelection'));
const PartDetailPage = React.lazy(
  () => import('pages/managment/parts/partDetailPage'),
);
const NewPart = React.lazy(() => import('pages/managment/newPart'));
const ShippedOrders = React.lazy(() => import('pages/reports/shippedOrders'));
const OpenOrders = React.lazy(() => import('pages/reports/openOrders'));
const ScheduledItemsWithoutStation = React.lazy(
  () => import('pages/reports/scheduledItemsWithoutStation'),
);
const UserActivity = React.lazy(() => import('pages/reports/userActivity'));
const EstimationDetail = React.lazy(
  () => import('pages/crm/estimation/estimationDetail'),
);
const Activity = React.lazy(() => import('pages/crm/activity/activity'));
const EstimationList = React.lazy(
  () => import('pages/crm/estimation/estimationList'),
);
const MangersActivityReport = React.lazy(
  () => import('pages/reports/mangersActivityReport'),
);
const Fabrication = React.lazy(() => import('pages/reports/fabrication'));
const BunCalcQty = React.lazy(() => import('pages/admin/bunCalcQty'));
const Manufacturer = React.lazy(() => import('pages/crm/manufacturer'));
const MakeupReport = React.lazy(() => import('pages/reports/makeupReport'));
const WeightEntry = React.lazy(() => import('pages/Others/weightEntry'));
const WidgetSetup = React.lazy(() => import('pages/Others/widget/widgetSetup'));
const BrushDetailPage = React.lazy(
  () => import('pages/webDetails/brush/brushDetailPage'),
);
const Brushes = React.lazy(() => import('pages/webDetails/brush/brushes'));
const Ecg_category = React.lazy(() => import('pages/webDetails/ecg_category'));
const TrackingReport = React.lazy(() => import('pages/reports/trackingReport'));
const CustomerOrderReport = React.lazy(
  () => import('pages/reports/customerOrderReport'),
);
const UtilizationReport = React.lazy(
  () => import('pages/reports/utilizationReport'),
);
const ProductionOpenOrders = React.lazy(
  () => import('pages/reports/productionOpenOrders'),
);
const CompanyBasedTrackingReport = React.lazy(
  () => import('pages/reports/companyBasedTrackingReport'),
);
const EditTimeClockPage = React.lazy(
  () => import('pages/timeClock/editTimeClockPage'),
);
const StaffOnSite = React.lazy(() => import('pages/timeClock/staffOnSite'));
const TimeClockReport = React.lazy(
  () => import('pages/timeClock/timeClockReport'),
);
const Map = React.lazy(() => import('pages/maps/map'));
const TimeCard = React.lazy(() => import('pages/user/timeCard'));
const StaffOffSite = React.lazy(() => import('pages/timeClock/staffOffSite'));
const WidthList = React.lazy(() => import('pages/admin/widthList'));
const VacationRequest = React.lazy(() => import('pages/user/vacationRequest'));
const VacationApproval = React.lazy(
  () => import('pages/admin/vacationApproval'),
);
const BlackoutDays = React.lazy(() => import('pages/admin/blackoutDays'));
const PartCategory = React.lazy(() => import('pages/admin/partCategory'));
const AveragePerDayReports = React.lazy(
  () => import('pages/reports/perDayToShip'),
);
const DaysToShipReports = React.lazy(() => import('pages/reports/dayToShip'));
const Kitslist = React.lazy(() => import('pages/managment/kits/kitslist'));
const ModifyKit = React.lazy(() => import('pages/managment/kits/modifyKit'));
const AssignedProductionTo = React.lazy(
  () => import('pages/Others/productionScreen/assignedProductionTo'),
);
const PermissionListPage = React.lazy(
  () => import('pages/admin/role/permissionListPage'),
);
const StaffData = React.lazy(() => import('pages/admin/staffData'));
const TimeClockNotices = React.lazy(
  () => import('pages/timeClock/timeClockNotices'),
);
const PartApprovalWorkflows = React.lazy(
  () => import('pages/partRequestAndApproval/partApprovalWorkflows'),
);
const PartRequest = React.lazy(
  () => import('pages/partRequestAndApproval/partRequest'),
);
const PartRequestDetails = React.lazy(
  () => import('pages/partRequestAndApproval/partRequestDetails'),
);
const PartCreation = React.lazy(
  () => import('pages/partRequestAndApproval/partCreation'),
);
const DraftPartList = React.lazy(
  () => import('pages/partRequestAndApproval/draftPartList'),
);
const PartApproval = React.lazy(
  () => import('pages/partRequestAndApproval/partApproval'),
);
const DraftPartDetails = React.lazy(
  () => import('pages/partRequestAndApproval/draftPartDetails'),
);
const ThompsonsDieBoard = React.lazy(
  () => import('pages/admin/thompsonsDieBoard'),
);
const Cut = React.lazy(() => import('pages/admin/cut'));
const Header = React.lazy(() => import('pages/admin/header'));
const Size = React.lazy(() => import('pages/admin/size'));
const Category = React.lazy(() => import('pages/hardware/category'));
const HardwareType = React.lazy(() => import('pages/hardware/type'));
const HardwareProduct = React.lazy(() => import('pages/hardware/product'));
const PartRequestSketch = React.lazy(
  () => import('pages/partRequestAndApproval/sketch'),
);
const HelpTextScreen = React.lazy(() => import('pages/admin/helpText'));
const WrappedComponent = React.lazy(
  () => import('pages/reports/mangersActivityReport'),
);
const NotificationList = React.lazy(
  () => import('pages/admin/notificationList'),
);
const UserNotificationList = React.lazy(
  () => import('pages/admin/notifications/userNotificationList'),
);
const NotificationRole = React.lazy(
  () => import('pages/admin/notifications/notificationRole'),
);
const WebUser = React.lazy(() => import('pages/crm/webUser'));
const Sites = React.lazy(() => import('pages/crm/sites/sites'));
const SiteDetails = React.lazy(() => import('pages/crm/sites/siteDetails'));
const HiLo = React.lazy(() => import('pages/warehouse/hi-lo'));
const Inventory = React.lazy(() => import('pages/warehouse/inventory'));
const RawParts = React.lazy(() => import('pages/warehouse/rawParts'));
const Rma = React.lazy(() => import('pages/warehouse/rma'));
const RmaDetail = React.lazy(() => import('pages/warehouse/rmaDetail'));
const RmaList = React.lazy(() => import('pages/warehouse/rmaList'));
const Skiving = React.lazy(() => import('pages/warehouse/skiving'));
const Warehouse = React.lazy(() => import('pages/warehouse/warehouse'));
const WorkingDay = React.lazy(() => import('pages/warehouse/workingDay'));
const InventoryReport = React.lazy(
  () => import('pages/warehouse/inventoryReport'),
);
const RackBin = React.lazy(() => import('pages/warehouse/rackBin'));
const Event = React.lazy(() => import('pages/admin/event'));
const QtyYield = React.lazy(() => import('pages/bandsaw/qtyYield'));
const LengthSettings = React.lazy(() => import('pages/bandsaw/lengthSettings'));
const WidthSettings = React.lazy(() => import('pages/bandsaw/widthSettings'));
const Testimonial = React.lazy(() => import('pages/admin/testimonial'));
const HowToVideos = React.lazy(() => import('pages/admin/howToVideos'));
const MonthlyWinners = React.lazy(() => import('pages/admin/monthlyWinners'));
const PartOpenOrderReport = React.lazy(
  () => import('pages/reports/partOpenOrderReport'),
);
const BrushType = React.lazy(() => import('pages/brush/brushType'));
const Brush = React.lazy(() => import('pages/brush/brush'));
const AssembleParts = React.lazy(
  () => import('pages/Others/productionScreen/assembleParts'),
);
const CheckListTemplate = React.lazy(
  () => import('pages/admin/checkListTemplate'),
);
const CheckListReport = React.lazy(
  () => import('pages/reports/checkListReport'),
);
const NLReportGenerator = React.lazy(
  () => import('pages/reports/NLReportGenerator'),
);

const PickRawGoods = React.lazy(() => import('pages/warehouse/pickRawGoods'));
const PlaceRawGoods = React.lazy(() => import('pages/warehouse/placeRawGoods'));

const StaffLeaveReset = React.lazy(
  () => import('pages/settings/staffLeaveReset'),
);
const CallRail = React.lazy(() => import('pages/crm/callRail'));
const CallRailBadCallReasons = React.lazy(
  () => import('pages/crm/callRailBadCallReasons'),
);
const TvDisplayPage = React.lazy(() => import('pages/tvDisplay/TvDisplayPage'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <PersistentLayout />,
    errorElement: <ErrorComponent />,
    children: [
      // Redirect root to dashboard
      {
        index: true,
        loader: () => {
          throw redirect(NavigationRoutes.dashboard);
        },
      },
      {
        path: NavigationRoutes.dashboard,
        element: <Dashboard />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.orderItem,
        element: <OrderItem />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.packagingItem,
        element: <PackagingItem />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.shipmentItem,
        element: <ShipmentItem />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.orderLookUpCustomerId,
        element: <OrderLookUp />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.materialList,
        element: <MaterialList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.colors,
        element: <Colors />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.carrierList,
        element: <CarrierList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.commodityDensityMapping,
        element: <CommodityDensityMappingList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.plyList,
        element: <PlyList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.roles,
        element: <RoleListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.productionProcess,
        element: <ProductionProcess />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.checkListTemplate,
        element: <CheckListTemplate />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.productionStation,
        element: <ProductionStationListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.modifyPartAssembly,
        element: <ModifyPartAssembly />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.parts,
        element: <PartsListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.customerMaster,
        element: <CustomerMasterList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.editCustomer,
        element: <EditCustomerPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.customerContact,
        element: <ContactListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partAssembly,
        element: <PartAssemblyList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.orderListPage,
        element: <OrderListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.productionScreen,
        element: <ProductionStatus />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.assembleParts,
        element: <AssembleParts />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.packagingScreen,
        element: <PackagingScreen />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.shippingScreen,
        element: <ShippingScreen />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.editRolePermission,
        element: <EditRolePermission />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.permission,
        element: <PermissionListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.changePassword,
        element: <ChangePassword />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.productionSchedule,
        element: <ProductionSchedule />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.attachments,
        element: <AttachmentListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.engineering,
        element: <PartsListPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.lead,
        element: <Leads />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.leadSource,
        element: (
          <PermissionRoute entity={PermissionEnum.LEAD_SOURCE_PERMISSION}>
            <Sources />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.opportunity,
        element: (
          <PermissionRoute entity={PermissionEnum.OPPORTUNITY_PERMISSION}>
            <Opportunity />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.activity,
        element: (
          <PermissionRoute entity={PermissionEnum.ACTIVITY_PERMISSION}>
            <Activity />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.holiday,
        element: <Holiday />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.modifyOpportunity,
        element: (
          <PermissionRoute entity={PermissionEnum.OPPORTUNITY_PERMISSION}>
            <ConvertToOpportunity />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.costMethod,
        element: <CostMethod />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.componetMaster,
        element: <ComponetMaster />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partComponent,
        element: <PartComponent />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.columnSelection,
        element: <ColumnSelection />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partDetails,
        element: <PartDetailPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.newPart,
        element: <NewPart />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.shippedOrders,
        element: <ShippedOrders />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.trackingReports,
        element: <TrackingReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.openOrders,
        element: <OpenOrders />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.scheduledItemsWithoutStation,
        element: <ScheduledItemsWithoutStation />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.productionOpenOrders,
        element: <ProductionOpenOrders />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.userActivity,
        element: <UserActivity />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.estimationDetails,
        element: <EstimationDetail />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.estimationList,
        element: <EstimationList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.managersActivityReport,
        element: <WrappedComponent />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.fabricationReport,
        element: <Fabrication />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.bunCalcQty,
        element: <BunCalcQty />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.crmManufacture,
        element: <Manufacturer />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.makeUpReport,
        element: <MakeupReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.weightEntry,
        element: <WeightEntry />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.widgetSetup,
        element: <WidgetSetup />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.shopItem,
        element: <Brushes />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.shopItemDetail,
        element: <BrushDetailPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.category,
        element: <Ecg_category />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.webOrders,
        element: <WebOrders />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.carouselImages,
        element: <CarouselImages />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.customerOrderReport,
        element: <CustomerOrderReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.utilizationReport,
        element: <UtilizationReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.companyTrackingReports,
        element: <CompanyBasedTrackingReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.editTimeClock,
        element: <EditTimeClockPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.staffData,
        element: <StaffData />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.editStaff,
        element: <EditStaffPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.staffPoints,
        element: <StaffPointsPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.staffOnSite,
        element: <StaffOnSite />,
        errorElement: <ErrorComponent />,
      },

      {
        path: NavigationRoutes.timeClockReport,
        element: <TimeClockReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.customerMap,
        element: <Map />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.timeCard,
        element: <TimeCard />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.staffOffSite,
        element: <StaffOffSite />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.width,
        element: <WidthList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.vacationRequest,
        element: <VacationRequest />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.vacationApproval,
        element: <VacationApproval />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.blackoutDays,
        element: <BlackoutDays />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.orderRegions,
        element: <OrderRegions />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.customerRegions,
        element: <CustomerRegions />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partCategory,
        element: <PartCategory />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.perDayToShipReport,
        element: <AveragePerDayReports />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.daysToShipReport,
        element: <DaysToShipReports />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.kitsList,
        element: <Kitslist />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.modifyKit,
        element: <ModifyKit />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.assignedProductionTo,
        element: <AssignedProductionTo />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partApprovalWorkflow,
        element: <PartApprovalWorkflows />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partRequest,
        element: <PartRequest />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.timeClockNotices,
        element: <TimeClockNotices />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partRequestDetails,
        element: <PartRequestDetails />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partCreation,
        element: <PartCreation />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.draftPartList,
        element: <DraftPartList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partApproval,
        element: <PartApproval />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.draftPartDetails,
        element: <DraftPartDetails />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.currentDayManagersActivityReport,
        element: <MangersActivityReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.thompsonsDieBoard,
        element: <ThompsonsDieBoard />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.cut,
        element: <Cut />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.header,
        element: <Header />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.size,
        element: <Size />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.hardware + NavigationRoutes.categorys,
        element: <Category />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.hardware + NavigationRoutes.type,
        element: <HardwareType />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.hardware + NavigationRoutes.product,
        element: <HardwareProduct />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.brush + NavigationRoutes.brushes,
        element: <Brush />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.brush + NavigationRoutes.brushType,
        element: <BrushType />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.sketch,
        element: <PartRequestSketch />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.helpText,
        element: <HelpTextScreen />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.notificationList,
        element: <NotificationList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.userNotifications,
        element: <UserNotificationList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.notificationRole,
        element: <NotificationRole />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.webUser,
        element: (
          <PermissionRoute entity={PermissionEnum.WEB_USER_PERMISSION}>
            <WebUser />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.sites,
        element: (
          <PermissionRoute entity={PermissionEnum.SITES_PERMISSION}>
            <Sites />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.siteDetails,
        element: (
          <PermissionRoute entity={PermissionEnum.SITES_PERMISSION}>
            <SiteDetails />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.hiLo,
        element: <HiLo />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.workingDay,
        element: <WorkingDay />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.warehouse,
        element: <Warehouse />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.inventory,
        element: <Inventory />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.inventoryReport,
        element: <InventoryReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.pickRawGoods,
        element: <PickRawGoods />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.placeRawGoods,
        element: <PlaceRawGoods />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.rma,
        element: <Rma />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.rmaDetail,
        element: <RmaDetail />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.rmaList,
        element: <RmaList />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.rawParts,
        element: <RawParts />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.skiving,
        element: <Skiving />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.rackBin,
        element: <RackBin />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.event,
        element: <Event />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.qtyYield,
        element: <QtyYield />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.lengthSettings,
        element: <LengthSettings />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.widthSettings,
        element: <WidthSettings />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.testimonial,
        element: <Testimonial />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.howToVideo,
        element: <HowToVideos />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.monthlyWinners,
        element: <MonthlyWinners />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.partOpenOrderReport,
        element: <PartOpenOrderReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.checkListReport,
        element: <CheckListReport />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.nlReportGenerator,
        element: <NLReportGenerator />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.settings + NavigationRoutes.staffLeaveReset,
        element: <StaffLeaveReset />,
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.callRail,
        element: (
          <PermissionRoute entity={PermissionEnum.CALL_RAIL_PERMISSION}>
            <CallRail />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: NavigationRoutes.callRailBadCallReason,
        element: (
          <PermissionRoute entity={PermissionEnum.CALL_RAIL_PERMISSION}>
            <CallRailBadCallReasons />
          </PermissionRoute>
        ),
        errorElement: <ErrorComponent />,
      },
    ],
  },
  // Public routes (no layout)
  {
    path: NavigationRoutes.login,
    element: <Login />,
    errorElement: <ErrorComponent />,
  },
  {
    path: NavigationRoutes.tvDisplay,
    element: <TvDisplayPage />,
    errorElement: <ErrorComponent />,
  },
  // Walkup public route
  {
    path: NavigationRoutes.walkupLogin,
    element: <WalkupLogin />,
    errorElement: <ErrorComponent />,
  },
  // Walkup authenticated routes (no sidebar)
  {
    path: '/w',
    element: <WalkupLayout />,
    errorElement: <ErrorComponent />,
    children: [
      {
        path: 'Dashboard',
        element: <Dashboard />,
        errorElement: <ErrorComponent />,
      },
      {
        path: 'time-card',
        element: <TimeCard />,
        errorElement: <ErrorComponent />,
      },
      {
        path: 'vacation-request',
        element: <VacationRequest />,
        errorElement: <ErrorComponent />,
      },
      {
        path: 'staff-points',
        element: <StaffPointsPage />,
        errorElement: <ErrorComponent />,
      },
      {
        path: 'change-password',
        element: <ChangePassword />,
        errorElement: <ErrorComponent />,
      },
      {
        path: 'notification-list',
        element: <NotificationList />,
        errorElement: <ErrorComponent />,
      },
    ],
  },
]);

const Route = () => {
  const customerManager = useCustomerManager();

  return (
    <CustomerContext.Provider value={customerManager}>
      <React.Suspense fallback={<Loader show />}>
        <RouterProvider router={router} />
      </React.Suspense>{' '}
    </CustomerContext.Provider>
  );
};

export default Route;
